import { MarkdownViewer } from '@/business-components/markdown-viewer/MarkdownViewer';
import { ContainerQuery } from '@/components/container-query/ContainerQuery';
import { CustomTag } from '@/components/custom-tag/CustomTag';
import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import type { HttpApiServerRequestBodyType } from '@/services/http-api/httpApi';
import { ApiDocLayout } from '@/shared/constant/layout/ApiDocLayout';
import { getDefaultSchema } from '@/shared/util/getDefaultSchema';
import { HttpApiRequestBodyType, ParametersLocationType } from '@tarslib/types/lib/httpApi';
import { ParameterDisplayMode } from '@tarslib/types/lib/projectSetting';
import { Card } from 'aui';
import clsx from 'clsx';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { ParametersCard } from '../parameters-card/ParametersCard';
import parametersCardStyles from '../parameters-card/ParametersCard.module.css';
import styles from './RequestBody.module.css';
import { MultiExamples } from './components/multi-examples/MultiExamples';

export type RequestBodyProps = {
  value: HttpApiServerRequestBodyType;
};

export function RequestBody(props: RequestBodyProps) {
  const { value } = props;
  const { type, parameters = [], jsonSchema, mediaType, description, examples } = value;

  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);
  const apiDocLayout = useDocsDataModelStore((x) => x.docsBaseConfig.apiDocLayout);

  return (
    <>
      {(type === HttpApiRequestBodyType.FormData ||
        type === HttpApiRequestBodyType.XWwwFormUrlencoded) && (
        <ParametersCard
          viewMode="write"
          type={ParametersLocationType.Body}
          value={parameters}
          title={
            <div>
              <span>{clientI18n('message.bodyParameter')}</span>
              <CustomTag className="ml-2">
                {type === HttpApiRequestBodyType.FormData
                  ? 'multipart/form-data'
                  : 'application/x-www-form-urlencoded'}
              </CustomTag>
            </div>
          }
        />
      )}

      {(type === HttpApiRequestBodyType.Xml ||
        type === HttpApiRequestBodyType.Json ||
        type === HttpApiRequestBodyType.MsgPack) && (
        <ParametersCard
          viewMode="write"
          type={ParametersLocationType.Body}
          mode={ParameterDisplayMode.MODERN}
          value={jsonSchema ?? getDefaultSchema()}
          className={styles['parameters-json-card']}
          title={
            <div className="flex items-center">
              <span>{clientI18n('message.bodyParameter')}</span>
              <CustomTag className="ml-2">
                {mediaType ||
                  (type === HttpApiRequestBodyType.Json || type === HttpApiRequestBodyType.MsgPack
                    ? `${type}`
                    : `application/xml`)}
              </CustomTag>
            </div>
          }
          cardBodyRender={(originNode) => {
            return (
              <ContainerQuery containerSizes={[880]} className="@container/request-schema-card">
                <PanelGroup
                  direction="horizontal"
                  className={clsx('!block', {
                    ['@[880px]/request-schema-card:!flex']: apiDocLayout === ApiDocLayout.OneColumn,
                  })}
                >
                  <Panel defaultSize={60} minSize={50} maxSize={70} className="px-4 py-4">
                    {originNode}
                  </Panel>
                  <PanelResizeHandle
                    className={clsx('hidden w-[1px] bg-border-color-split', {
                      ['@[880px]/request-schema-card:block']:
                        apiDocLayout === ApiDocLayout.OneColumn,
                    })}
                  />
                  <Panel
                    defaultSize={40}
                    className={clsx('border-t border-color-split', {
                      ['@[880px]/request-schema-card:border-t-0']:
                        apiDocLayout === ApiDocLayout.OneColumn,
                    })}
                  >
                    <MultiExamples value={examples} type={type} />
                  </Panel>
                </PanelGroup>
              </ContainerQuery>
            );
          }}
        />
      )}

      {type === HttpApiRequestBodyType.Raw && (!!description || !!examples.length) && (
        <ParametersCard
          viewMode="write"
          type={ParametersLocationType.Body}
          value={getDefaultSchema()}
          className={styles['parameters-json-card']}
          hiddenExtra={true}
          title={
            <div className="flex items-center">
              <span>{clientI18n('message.bodyParameter')}</span>
              <CustomTag className="ml-2">{mediaType || 'text/plain'}</CustomTag>
            </div>
          }
          cardBodyRender={() => {
            return (
              <ContainerQuery containerSizes={[880]} className="@container/request-raw-card">
                <PanelGroup
                  direction="horizontal"
                  className={clsx('!block', {
                    ['@[880px]/request-raw-card:!flex']: apiDocLayout === ApiDocLayout.OneColumn,
                  })}
                >
                  {description && (
                    <Panel defaultSize={60} minSize={50} maxSize={70} className="px-4 py-4">
                      <MarkdownViewer
                        value={description}
                        enableHeaderPlugin={false}
                        inDocView={false}
                      />
                    </Panel>
                  )}
                  {!!description && !!examples.length && (
                    <PanelResizeHandle
                      className={clsx('hidden w-[1px] bg-border-color-split', {
                        ['@[880px]/request-raw-card:block']:
                          apiDocLayout === ApiDocLayout.OneColumn,
                      })}
                    />
                  )}
                  {!!examples.length && (
                    <Panel
                      defaultSize={40}
                      className={clsx('border-t border-color-split', {
                        ['@[880px]/request-raw-card:border-t-0']:
                          apiDocLayout === ApiDocLayout.OneColumn,
                      })}
                    >
                      <MultiExamples value={examples} type={type} />
                    </Panel>
                  )}
                </PanelGroup>
              </ContainerQuery>
            );
          }}
        />
      )}

      {type === HttpApiRequestBodyType.Binary && (
        <Card
          size="small"
          title={
            <div>
              <span>{clientI18n('message.bodyParameter')}</span>
              <CustomTag className="ml-2">{mediaType || 'application/octet-stream'}</CustomTag>
            </div>
          }
          className={parametersCardStyles['parameters-card']}
        >
          <div className="flex items-center justify-center">
            <span className="w-fit text-sm text-secondary">
              {examples[0]?.value
                ? clientI18n('api.requestBody.binary.uploaded')
                : clientI18n('api.requestBody.binary.notConfigured')}
            </span>
          </div>
        </Card>
      )}

      {type === HttpApiRequestBodyType.GraphQL && (
        <Card
          size="small"
          title={
            <div>
              <span>{clientI18n('message.bodyParameter')}</span>
              <CustomTag className="ml-2">{mediaType || 'application/json'}</CustomTag>
            </div>
          }
          className={parametersCardStyles['parameters-card']}
        >
          <div className="flex items-center justify-center">
            <span className="w-fit text-sm text-secondary">
              {clientI18n('api.parameterLabel.noNeedToDefineTheBody')}
            </span>
          </div>
        </Card>
      )}
    </>
  );
}
